import { cn } from '../../../lib/utils';
import {
  deriveStructuralAvailability,
  type ListStructuralContextResponse,
  type StructuralAvailability,
  type StructuralContextBlock,
} from '../../../api/intelClient';

interface StructuralContextPanelProps {
  /** Hook payload — `null` for the silent-skip / 401+503 path. */
  data: ListStructuralContextResponse | null | undefined;
  /** True while TanStack Query is mid-fetch on first load. */
  isLoading: boolean;
  /** Hard error (network, 5xx other than 503). 401+503 are pre-handled in
   * the fetcher (`{ data: null, silentSkip: true }`) — never seen here. */
  error: unknown;
  className?: string;
}

/**
 * Structural Context panel — cycle-114 sprint-213 Tasks 3.5 (AC-12).
 *
 * A minimal, embedded, read-only rail for the non-scored Lane-3 structural
 * priors (country resilience CRI + cable-health). Mirrors the sibling
 * `ExternalIntelligencePanel` (pure-on-data; the mount feeds the hook payload
 * in) and reuses its achromatic visual language — NO invented visual language
 * (`output/design_reference/` is source of truth).
 *
 * Load-bearing honesty commitments (AC-12 hard exclusions):
 *  - The `STRUCTURAL PRIOR · NOT SCORED` badge is achromatic — chroma would
 *    imply a severity/scored signal. CRI never enters Echelon scoring (§3.8).
 *  - No ranking table, no 220-country browser, no weighting controls, no
 *    alert/live-risk semantics. Copy stays resilience-phrased ("resilience
 *    moderate"), never "spike"/"alert"/"severity".
 *  - Honest Sparse / Unavailable / Gap / Empty states — never fake completeness.
 */
export function StructuralContextPanel({
  data,
  isLoading,
  error,
  className,
}: StructuralContextPanelProps) {
  // Silent-skip — flag off (503) or unauthenticated (401). Render no DOM.
  if (data === null && !isLoading && !error) {
    return null;
  }

  if (error) {
    return (
      <PanelShell className={className} availability="unavailable">
        <p
          role="alert"
          className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)] px-2 py-3"
        >
          structural context unavailable
        </p>
      </PanelShell>
    );
  }

  if (isLoading && !data) {
    return (
      <PanelShell className={className} availability="unavailable">
        <p
          role="status"
          aria-live="polite"
          className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)] px-2 py-3"
        >
          loading structural context…
        </p>
      </PanelShell>
    );
  }

  if (!data) return null;

  return <PanelBody data={data} className={className} />;
}

function PanelBody({
  data,
  className,
}: {
  data: ListStructuralContextResponse;
  className?: string;
}) {
  // The reader already returns a total-ordered, bounded set (as_of desc →
  // confidence → id), so we preserve order rather than re-sort. There is no
  // severity ordering here by design — structural priors are not ranked.
  const blocks = data.blocks;
  const availability: StructuralAvailability = deriveStructuralAvailability(blocks);

  return (
    <PanelShell
      className={className}
      availability={availability}
      rowCount={blocks.length}
      truncated={data.truncated}
      crossVersion={data.cross_version}
    >
      {blocks.length === 0 ? (
        <p
          role="status"
          className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)] px-2 py-3"
        >
          no structural context available
        </p>
      ) : (
        <ul aria-label="Structural context blocks" className="flex flex-col gap-2">
          {blocks.map((block) => (
            <StructuralContextRow key={block.id} block={block} />
          ))}
        </ul>
      )}
    </PanelShell>
  );
}

// ─── Per-block row ───────────────────────────────────────────────────

function StructuralContextRow({ block }: { block: StructuralContextBlock }) {
  const identity = block.country_name || block.country_code || '—';
  const isCri = block.context_type === 'cri';

  return (
    <li
      data-testid="structural-context-row"
      data-country={block.country_code ?? '_'}
      data-context-type={block.context_type}
      className="flex flex-col gap-1 rounded-md border border-[var(--e-border-secondary)]/60 px-3 py-2"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--e-text-primary)]">
          {identity}
        </span>
        <span
          className="text-[9px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]/80"
          data-testid="structural-context-calibration"
        >
          {block.calibration_status}
        </span>
      </div>

      {isCri ? <CriBody block={block} /> : <CableHealthBody />}

      {block.gap_kind && (
        <span
          data-testid="structural-context-gap"
          className="text-[9px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]"
        >
          gap: {block.gap_kind.replace('_', ' ').toLowerCase()}
        </span>
      )}

      <CoverageFreshness block={block} />
    </li>
  );
}

function CriBody({ block }: { block: StructuralContextBlock }) {
  const hasScore =
    block.cri_score != null && block.score_band != null && block.score_band !== 'unavailable';
  const pillars = block.pillar_scores
    ? Object.entries(block.pillar_scores).slice(0, 3)
    : [];

  return (
    <div className="flex flex-col gap-0.5">
      <span
        data-testid="structural-context-cri"
        className="text-[10px] font-mono text-[var(--e-text-muted)]"
      >
        {hasScore
          ? `resilience ${block.score_band} · CRI ${block.cri_score!.toFixed(1)}/100`
          : 'CRI unavailable'}
      </span>
      {pillars.length > 0 ? (
        <span
          data-testid="structural-context-pillars"
          className="text-[9px] font-mono text-[var(--e-text-muted)]/80"
        >
          {pillars.map(([k, v]) => `${k} ${Math.round(v)}`).join(' · ')}
        </span>
      ) : (
        <span
          data-testid="structural-context-pillars"
          className="text-[9px] font-mono italic text-[var(--e-text-muted)]/70"
        >
          pillars unavailable
        </span>
      )}
    </div>
  );
}

function CableHealthBody() {
  return (
    <span
      data-testid="structural-context-cable-health"
      className="text-[10px] font-mono text-[var(--e-text-muted)]"
    >
      structural cable-health context
    </span>
  );
}

function CoverageFreshness({ block }: { block: StructuralContextBlock }) {
  const cov = block.coverage;
  const freshness = block.lineage?.freshness;
  const parts: string[] = [];
  if (cov) parts.push(`coverage ${(cov.source_coverage * 100).toFixed(0)}%`);
  if (freshness) parts.push(freshness);
  if (parts.length === 0) parts.push('coverage —');

  return (
    <div className="flex items-center gap-2">
      <span
        data-testid="structural-context-coverage"
        className="text-[9px] font-mono text-[var(--e-text-muted)]/70"
      >
        {parts.join(' · ')}
      </span>
      {cov?.low_confidence && (
        <span
          data-testid="structural-context-low-confidence"
          className="text-[9px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]"
        >
          low confidence
        </span>
      )}
    </div>
  );
}

// ─── PanelShell (inline, mirrors ExternalIntelligencePanel) ──────────

interface PanelShellProps {
  availability: StructuralAvailability;
  rowCount?: number;
  truncated?: boolean;
  crossVersion?: boolean;
  className?: string;
  children: React.ReactNode;
}

function PanelShell({
  availability,
  rowCount,
  truncated,
  crossVersion,
  className,
  children,
}: PanelShellProps) {
  return (
    <section
      aria-label="Structural context"
      data-echelon-scored="false"
      data-testid="structural-context-panel"
      data-availability={availability}
      className={cn(
        'flex flex-col gap-2',
        'rounded-lg border border-[var(--e-border-secondary)]',
        'bg-[var(--e-bg-card)]/85 backdrop-blur-md',
        'p-4',
        className,
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]">
            Structural context
          </span>
          <span className="text-[10px] font-mono italic text-[var(--e-text-muted)]/80">
            (slow-moving structural priors — not live events)
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {typeof rowCount === 'number' && (
            <span
              className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]"
              data-testid="structural-context-row-count"
            >
              {rowCount} {rowCount === 1 ? 'block' : 'blocks'}
            </span>
          )}
          <NotScoredBadge />
        </div>
      </header>

      {/* Honest, non-silent truncation (§5.1) + cross-version flag (§3.9). */}
      {truncated && (
        <p
          data-testid="structural-context-truncated"
          className="text-[9px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]/80"
        >
          showing a bounded subset — more structural context available
        </p>
      )}
      {crossVersion && (
        <p
          data-testid="structural-context-cross-version"
          className="text-[9px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]/80"
        >
          spans multiple calculation versions — comparisons flagged
        </p>
      )}

      {children}
    </section>
  );
}

/** STRUCTURAL PRIOR · NOT SCORED — the panel's honesty anchor. Deliberately
 * ACHROMATIC (no iris, no band hue): chroma would read as a scored/severity
 * signal, and CRI/cable-health context never enters Echelon scoring (§3.8). */
function NotScoredBadge() {
  return (
    <span
      data-testid="structural-context-not-scored-badge"
      role="note"
      aria-label="Structural prior, not scored by Echelon"
      className={cn(
        'inline-flex items-center px-2 py-0.5',
        'rounded-sm text-[9px] font-mono uppercase tracking-[0.12em]',
        'border border-[var(--e-border-secondary)]',
        'text-[var(--e-text-muted)]',
      )}
    >
      Structural prior · Not scored
    </span>
  );
}
